import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  IconButton,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
  Chip,
} from "@mui/material";
import { Add, Edit, Visibility } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { getTenantsList } from "../../api/tenant";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";

function TenantCard({ tenant, isSuperAdmin, onView, onEdit }) {
  return (
    <MobileListCard
      title={tenant.name}
      subtitle={`ID ${tenant.id}`}
      status={
        <Chip
          label={tenant.is_active ? "Active" : "Inactive"}
          color={tenant.is_active ? "success" : "default"}
          size="small"
        />
      }
      fields={[
        {
          label: "Created",
          value: tenant.created_at
            ? new Date(tenant.created_at).toLocaleDateString()
            : "—",
        },
      ]}
      actions={
        <>
          <IconButton aria-label="View tenant" onClick={() => onView(tenant.id)}>
            <Visibility />
          </IconButton>
          {isSuperAdmin && (
            <IconButton aria-label="Edit tenant" onClick={() => onEdit(tenant.id)}>
              <Edit />
            </IconButton>
          )}
        </>
      }
    />
  );
}

const TenantList = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const isMobile = useIsMobileList("md");
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.includes("super_admin");

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setLoading(true);
        const response = await getTenantsList({
          skip: page * rowsPerPage,
          limit: rowsPerPage,
          include_total: true,
        });
        if (response && Array.isArray(response.items)) {
          setTenants(response.items);
          setTotalCount(response.total ?? 0);
        } else if (Array.isArray(response)) {
          setTenants(response);
          setTotalCount(response.length);
        } else {
          setTenants([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch tenants:", error);
        setTenants([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={APP_HOME_PATH}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Tenants</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4">Tenant management</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/admin/tenants/create")}
            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            Create tenant
          </Button>
        )}
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <MobileListCardSkeleton key={i} />
              ))
            : tenants.length === 0
              ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No organizations found.
                </Typography>
              )
              : tenants.map((tenant) => (
                  <TenantCard
                    key={tenant.id}
                    tenant={tenant}
                    isSuperAdmin={isSuperAdmin}
                    onView={(id) => navigate(`/admin/tenants/${id}`)}
                    onEdit={(id) => navigate(`/admin/tenants/${id}/edit`)}
                  />
                ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          {loading && (
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No organizations found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {tenants.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>{tenant.id}</TableCell>
                  <TableCell>{tenant.name}</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        color: tenant.is_active ? "success.main" : "error.main",
                        fontWeight: 600,
                      }}
                    >
                      {tenant.is_active ? "Active" : "Inactive"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {tenant.created_at
                      ? new Date(tenant.created_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="View tenant"
                      onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                    >
                      <Visibility />
                    </IconButton>
                    {isSuperAdmin && (
                      <IconButton
                        aria-label="Edit tenant"
                        onClick={() =>
                          navigate(`/admin/tenants/${tenant.id}/edit`)
                        }
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default TenantList;
